import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Avatar } from "./Avatar";
import { Skeleton } from "./Skeleton";

/**
 * Per-task activity timeline. Renders the raw activity_log rows returned by
 * GET /api/tasks/:taskId/activity (camelCase) using the timeline / tl-item
 * markup from docs/themes/default/TaskDetail.html.
 *
 * Distinct from <ActivityFeed> (project-scope, grouped + filtered) — this
 * one is the embedded TaskDetail Activity tab and stays compact.
 */

function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffSec = Math.max(0, (Date.now() - t) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const days = Math.floor(diffSec / 86400);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const ACTION_LABEL: Record<string, string> = {
  "task.created": "created this task",
  "task.updated": "edited this task",
  "task.moved": "moved this task",
  "task.deleted": "deleted this task",
  "comment.added": "commented on this task",
};

function actionLabel(action: string, payloadJson: string): string {
  // task.moved carries `toStatus` — surface the destination column inline
  // because that's the most informative piece of the row.
  if (action === "task.moved") {
    try {
      const payload = JSON.parse(payloadJson) as {
        toStatus?: string;
        statusChanged?: boolean;
      };
      if (payload.statusChanged && payload.toStatus) {
        return `moved this task to ${payload.toStatus}`;
      }
    } catch {
      // fall through
    }
  }
  return ACTION_LABEL[action] ?? action;
}

export function TaskActivityFeed({ taskId }: { taskId: string }) {
  const q = useQuery({
    queryKey: queryKeys.taskActivity(taskId),
    queryFn: () => api.taskActivity(taskId),
  });

  if (q.isLoading) {
    return <Skeleton count={3} height={40} style={{ marginBottom: 6 }} />;
  }
  if (q.isError) {
    return (
      <p className="t-sm t-muted">Couldn't load activity. Try again later.</p>
    );
  }
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return <p className="t-sm t-muted">No activity yet.</p>;
  }

  return (
    <div className="timeline">
      {items.map((it) => (
        <div key={it.id} className="tl-item">
          <Avatar
            id={it.actorId}
            name={it.actorName ?? it.actorId}
            size="sm"
            image={it.actorImage}
          />
          <div>
            <div className="tl-body">
              <b>{it.actorName ?? it.actorId}</b>{" "}
              {actionLabel(it.action, it.payloadJson)}
            </div>
            <div className="tl-time">{relativeTime(it.createdAt)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
